<template>
  <div class="dialog-form-title-buttons">
    <div class="hidden-xs">
      <a
        v-for="item in itemsFiltered"
        :key="item.key"
        class="title-link title-link__border"
        :class="{ 'title-link__no-divider': !props.divider }"
        @click="item.click"
        :href="item.href"
        :target="item.target"
      >
        <ui-icon
          size="10"
          :class="item.meta.iconClass"
          :color="$colors.white"
          :icon="item.icon || item.iconAppend"
        />
        <span>{{ item.title }}</span>
      </a>
    </div>

    <ui-menu
      v-model="menu"
      :items="itemsFiltered"
      location="bottom start"
      :offset="[18, 16]"
    >
      <template #activator>
        <div
          :ripple="false"
          class="hidden-sm-and-up dialog-form-title-buttons__menu dialog-form-title-buttons__menu-activator"
        >
          <a
            :ripple="false"
            class="title-link title-link__border dialog-form-title-buttons__menu-activator-text"
          >
            <ui-icon
              class="dialog-form-title-buttons__icon"
              size="10"
              :color="$colors.white"
              icon="fas fa-question-circle"
            ></ui-icon>
            {{ t('Help') }}
          </a>

          <ui-chevron class="ml-4" color="inherit" :open="menu"></ui-chevron>
        </div>
      </template>
    </ui-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';

type HeaderActionItem = {
  key: string;
  title: string;
  icon?: string;
  iconAppend?: string;
  click: () => void;
  showCondition?: boolean;
  href?: string;
  target?: string;
  meta: {
    iconClass: string;
    props?: {
      href?: string;
      target?: string;
    };
  };
};

const props = withDefaults(
  defineProps<{
    readArticleUrl?: string;
    divider?: boolean;
  }>(),
  {
    readArticleUrl: '',
    divider: true,
  },
);

const { t } = useI18n();
const $colors = useColors();
const store = useStore();
const menu = ref(false);
const isIntercomOpen = ref(false);

const openLiveSupport = () => {
  const ff = (store.user as any)?.featureFlags ?? {};
  if (ff.chatwoot?.enabled) {
    (window as any).$chatwoot?.toggle('open');
    isIntercomOpen.value = true;
    return;
  }
  (window as any).$intercom?.show();
  (window as any).$intercom?.boot({ hide_default_launcher: false });
  isIntercomOpen.value = true;
};

const closeLiveSupport = () => {
  const ff = (store.user as any)?.featureFlags ?? {};
  if (ff.chatwoot?.enabled) {
    (window as any).$chatwoot?.toggle('close');
    isIntercomOpen.value = false;
    return;
  }
  (window as any).$intercom?.hide();
  (window as any).$intercom?.boot({ hide_default_launcher: true });
  isIntercomOpen.value = false;
};

const actions = computed(() => ({
  readArticle: {
    key: 'read-article',
    title: t('Read Guide'),
    iconAppend: 'fas fa-question-circle',
    click: () => {},
    showCondition: !!props.readArticleUrl,
    href: props.readArticleUrl,
    target: '_blank',
    meta: {
      iconClass: 'dialog-form-title-buttons__icon',
      props: {
        href: props.readArticleUrl,
        target: '_blank',
      },
    },
  },
  openLiveSupport: {
    key: 'open-live-support',
    title: t('Live Support'),
    iconAppend: 'fas fa-comments',
    click: () => openLiveSupport(),
    showCondition: !isIntercomOpen.value,
    meta: {
      iconClass: 'dialog-form-title-buttons__icon',
    },
  },
  closeLiveSupport: {
    key: 'close-live-support',
    title: t('Close live chat'),
    iconAppend: 'fas fa-times',
    click: () => closeLiveSupport(),
    showCondition: isIntercomOpen.value,
    meta: {
      iconClass: 'dialog-form-title-buttons__icon dialog-form-title-buttons__icon--close',
    },
  },
}));

const items = computed<HeaderActionItem[]>(() => [
  actions.value.readArticle,
  actions.value.openLiveSupport,
  actions.value.closeLiveSupport,
]);

const itemsFiltered = computed(() =>
  items.value.filter(
    item => typeof item.showCondition === 'undefined' || item.showCondition,
  ),
);
</script>

<style lang="scss">
.dialog-form-title-buttons {
  display: inline-flex;
  flex-wrap: wrap;

  .title-link {
    display: inline-flex;
    align-items: center;

    text-decoration: none;
    color: var(--color-white);
    font-size: 13px;
    font-weight: normal;
    flex-grow: 1;
    text-align: right;
    padding-right: 26px;
    .fa-comment {
      padding-right: 5px;
      padding-bottom: 1px;
    }
    .fa-tachometer-alt {
      padding: 0 5px 2px 0;
    }
  }
}

.dialog-form-title-buttons .dialog-form-title-buttons__icon.ui-icon {
  margin-bottom: 0;
}

.dialog-form-title-buttons .dialog-form-title-buttons__icon--close.ui-icon {
  font-size: 12px;
  margin-bottom: 1px;
}

.dialog-form-title-buttons__menu {
  padding-right: 26px;
}

.dialog-form-title-buttons__menu-activator {
  display: inline-flex;
  flex-wrap: nowrap;
  color: var(--color-white);
}

.dialog-form-title-buttons__menu-activator-text {
  white-space: nowrap;
  padding-right: 0 !important;
  color: inherit;
  display: flex;
  align-items: center;
}

.dialog-form-title-buttons__menu-item {
  .dialog-form-title-buttons__icon {
    font-size: 10px;
    margin-bottom: 3px;
  }

  .dialog-form-title-buttons__icon--close {
    font-size: 12px;
    margin-bottom: 1px;
  }
}

.dialog-form-title-buttons .title-link__no-divider:after {
  content: none;
}
</style>
